/**
 * LibraryPickerModal.jsx — Sélecteur d'exercices visuel et unifié pour les créateurs de
 * programme (avancé + assisté) : recherche + filtres sur la grande bibliothèque Body-Up
 * (~450 exercices, photos/vidéos Sora 2) ET onglet "Mes exercices créés" (bibliothèque
 * personnalisée IA du client) — pour que le client retrouve facilement ses propres
 * exercices sans jamais avoir à retaper leur nom.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Loader2, Plus, ImageOff, Sparkles, Video, Dumbbell } from 'lucide-react';
import { useClientAuth } from '../../../context/ClientAuthContext';

const stripAccents = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const LibraryPickerModal = ({ en, onAdd, onClose, initialMuscleGroup = '' }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [tab, setTab] = useState('library');
  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState([]);
  const [customExercises, setCustomExercises] = useState([]);
  const [customLoaded, setCustomLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState(initialMuscleGroup || 'all');

  useEffect(() => {
    setLoading(true);
    authFetch('/api/exercises?limit=1000')
      .then((r) => r.json())
      .then((d) => setExercises(d.exercises || []))
      .catch(() => setExercises([]))
      .finally(() => setLoading(false));
  }, [authFetch]);

  useEffect(() => {
    if (tab !== 'custom' || customLoaded) return;
    authFetch('/api/tracking/custom-exercises')
      .then((r) => (r.ok ? r.json() : { exercises: [] }))
      .then((d) => setCustomExercises(d.exercises || []))
      .catch(() => setCustomExercises([]))
      .finally(() => setCustomLoaded(true));
  }, [tab, customLoaded, authFetch]);

  const muscleGroups = useMemo(() => {
    const set = new Set(exercises.map((e) => e.muscle_group).filter(Boolean));
    return Array.from(set).sort();
  }, [exercises]);

  const filtered = useMemo(() => {
    const q = stripAccents(search.trim());
    let list = exercises.filter((e) => (groupFilter === 'all' || e.muscle_group === groupFilter));
    if (q) list = list.filter((e) => stripAccents(e.name_fr || e.name).includes(q));
    return list.slice(0, 200);
  }, [exercises, groupFilter, search]);

  const filteredCustom = useMemo(() => {
    const q = stripAccents(search.trim());
    if (!q) return customExercises;
    return customExercises.filter((e) => stripAccents(e.name).includes(q));
  }, [customExercises, search]);

  const pickLibrary = (ex) => onAdd({
    name: en ? (ex.name_en || ex.name_fr) : ex.name_fr, muscle_group: ex.muscle_group,
    equipment: ex.equipment, image_url: ex.image_url, video_url: ex.video_status === 'ready' ? ex.video_url : null,
    id: ex.id, name_fr: ex.name_fr, type: ex.type, rest_default_sec: ex.rest_default_sec,
    tempo_default: ex.tempo_default, video_status: ex.video_status, video_disabled: ex.video_disabled,
    execution: ex.execution, tips: ex.tips,
  });
  const pickCustom = (ex) => onAdd({
    name: ex.name, muscle_group: ex.muscle_group || L('Personnalisé', 'Custom'), equipment: ex.equipment || '',
    image_url: ex.media_type === 'image' ? ex.media_url : null, video_url: ex.media_type === 'video' ? ex.media_url : null,
    is_custom: true, id: ex.id, name_fr: ex.name,
  });

  return (
    <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="library-picker-modal">
      <div className="w-full sm:max-w-xl sm:rounded-3xl bg-zinc-900 border border-white/10 rounded-t-3xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-white/10 shrink-0">
          <h3 className="font-black text-white text-base flex items-center gap-2"><Dumbbell className="w-4.5 h-4.5 text-primary" /> {L('Choisir un exercice', 'Choose an exercise')}</h3>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-white" data-testid="library-picker-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1.5 px-4 pt-3 shrink-0">
          <button onClick={() => setTab('library')} data-testid="library-picker-tab-library"
            className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${tab === 'library' ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-white/[0.03] border-white/10 text-zinc-400'}`}>
            {L('Bibliothèque officielle', 'Official library')}
          </button>
          <button onClick={() => setTab('custom')} data-testid="library-picker-tab-custom"
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${tab === 'custom' ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-white/[0.03] border-white/10 text-zinc-400'}`}>
            <Sparkles className="w-3.5 h-3.5" /> {L('Mes exercices créés', 'My created exercises')}
          </button>
        </div>

        <div className="p-3 space-y-2.5 border-b border-white/10 shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={L('Rechercher un exercice...', 'Search an exercise...')}
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm placeholder:text-zinc-600" data-testid="library-picker-search-input" />
          </div>
          {tab === 'library' && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
              <button onClick={() => setGroupFilter('all')} data-testid="library-picker-group-all"
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${groupFilter === 'all' ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-white/[0.04] border-white/10 text-zinc-400'}`}>
                {L('Tout', 'All')}
              </button>
              {muscleGroups.map((g) => (
                <button key={g} onClick={() => setGroupFilter(g)} data-testid={`library-picker-group-${g}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${groupFilter === g ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-white/[0.04] border-white/10 text-zinc-400'}`}>
                  {g}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {tab === 'library' ? (
            <>
              {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
              {!loading && filtered.length === 0 && <p className="text-center text-sm text-zinc-500 py-10">{L('Aucun exercice trouvé.', 'No exercise found.')}</p>}
              {filtered.map((ex) => (
                <button key={ex.id} onClick={() => pickLibrary(ex)}
                  className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-primary/30 p-2.5 text-left transition-colors"
                  data-testid={`library-picker-item-${ex.id}`}>
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center relative">
                    {ex.image_url ? <img src={ex.image_url} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-zinc-600" />}
                    {ex.video_status === 'ready' && <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center"><Video className="w-2 h-2 text-white" /></span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{en ? (ex.name_en || ex.name_fr) : ex.name_fr}</p>
                    <p className="text-[10px] text-zinc-500">{ex.muscle_group} · {ex.equipment}</p>
                  </div>
                  <Plus className="w-5 h-5 text-zinc-500 shrink-0" />
                </button>
              ))}
            </>
          ) : (
            <>
              {!customLoaded && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-amber-400" /></div>}
              {customLoaded && filteredCustom.length === 0 && (
                <p className="text-center text-sm text-zinc-500 py-10">{L("Aucun exercice personnalisé encore créé. Rendez-vous dans « Ma bibliothèque créée » pour en ajouter.", 'No custom exercise yet. Go to "My created library" to add one.')}</p>
              )}
              {filteredCustom.map((ex) => (
                <button key={ex.id} onClick={() => pickCustom(ex)}
                  className="w-full flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] hover:bg-amber-500/[0.08] hover:border-amber-500/40 p-2.5 text-left transition-colors"
                  data-testid={`library-picker-custom-item-${ex.id}`}>
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                    {ex.media_url && ex.media_type === 'image' ? <img src={ex.media_url} alt="" className="w-full h-full object-cover" />
                      : ex.media_type === 'video' ? <Video className="w-4 h-4 text-amber-400" /> : <Sparkles className="w-4 h-4 text-amber-500/60" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{ex.name}</p>
                    <p className="text-[10px] text-zinc-500">{ex.muscle_group || L('Personnalisé', 'Custom')}{ex.equipment ? ` · ${ex.equipment}` : ''}</p>
                  </div>
                  <Plus className="w-5 h-5 text-zinc-500 shrink-0" />
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibraryPickerModal;
